import React from 'react';
import type { FormikHelpers } from 'formik';
import { Formik } from 'formik';
import { isEmpty } from 'lodash';
import * as Yup from 'yup';

import { useStyles } from '~/assets/styles';
import CardDialog from '~/components/CardDialog';
import Button from '~/components/core/Atomic/Buttons/Button';
import Typography from '~/components/core/Atomic/Typography';
import CancelButton from '~/components/core/Buttons/CancelButton';
import type { UploadedFileData } from '~/components/SystemConfiguration/Communications/PhoneConfigurationTab/VoiceMessageConfigurationDialog/types';
import VoiceMessageConfigurationDialog from '~/components/SystemConfiguration/Communications/PhoneConfigurationTab/VoiceMessageConfigurationDialog/VoiceMessageConfigurationDialog';
import VoiceMessageDetailsPreview from '~/components/SystemConfiguration/Communications/PhoneConfigurationTab/VoiceMessageDetailsPreview/VoiceMessageDetailsPreview';
import type {
  PhoneProfileInfo,
  PhoneProfileInfoPhoneProfileVoiceMessageRef as VoiceMessageConfiguration,
} from '~/server_shared/openapi/data-contracts';

const FORM_IDS = {
  OUTBOUND_TEXT_OR_RECORDED_MESSAGE: 'outbound_text_or_recorded_message',
  OUTBOUND_GREETING_TEXT: 'outbound_greeting_text',
  OUTBOUND_VOICE_FILE: 'outbound_voice_file',
  IS_UPLOADING_OUTBOUND_VOICE_FILE: 'is_uploading_outbound_voice_file',
} as const;

interface FormValues {
  [FORM_IDS.OUTBOUND_TEXT_OR_RECORDED_MESSAGE]: 'text_message' | 'uploaded_text_message';
  [FORM_IDS.OUTBOUND_GREETING_TEXT]: string;
  [FORM_IDS.OUTBOUND_VOICE_FILE]?: UploadedFileData;
  [FORM_IDS.IS_UPLOADING_OUTBOUND_VOICE_FILE]?: boolean;
}

interface EditOutboundGreetingConfigDialogProps {
  configuration?: VoiceMessageConfiguration | null;
  onCancel: () => void;
  onSubmit: (params: { configuration_type: string; message_text?: string; stored_file_id?: number }) => Promise<void>;
}

const EditOutboundGreetingConfigDialog: React.FC<EditOutboundGreetingConfigDialogProps> = ({
  configuration,
  onCancel,
  onSubmit,
}) => {
  const { classes } = useStyles();
  const [allowSavingWithoutNewFile, setAllowSavingWithoutNewFile] = React.useState(
    !!configuration?.permanent_audio_file_url
  );

  const handleSubmit = async (values: FormValues, formikProps: FormikHelpers<FormValues>) => {
    try {
      await onSubmit({
        configuration_type: values?.outbound_text_or_recorded_message,
        message_text: values?.outbound_greeting_text,
        stored_file_id: values?.outbound_voice_file?.stored_file_id,
      });
      onCancel();
    } catch {
      formikProps.setSubmitting(false);
    }
  };

  return (
    <Formik<FormValues>
      initialValues={
        isEmpty(configuration)
          ? {
              [FORM_IDS.OUTBOUND_TEXT_OR_RECORDED_MESSAGE]: 'text_message',
              [FORM_IDS.OUTBOUND_GREETING_TEXT]: '',
              [FORM_IDS.OUTBOUND_VOICE_FILE]: undefined,
            }
          : {
              [FORM_IDS.OUTBOUND_TEXT_OR_RECORDED_MESSAGE]: configuration?.configuration_type ?? 'text_message',
              [FORM_IDS.OUTBOUND_GREETING_TEXT]: configuration?.message_text || '',
              [FORM_IDS.OUTBOUND_VOICE_FILE]: undefined,
            }
      }
      validationSchema={Yup.object().shape({
        [FORM_IDS.OUTBOUND_TEXT_OR_RECORDED_MESSAGE]: Yup.string()
          .oneOf(['text_message', 'uploaded_text_message'])
          .required(),
        [FORM_IDS.OUTBOUND_GREETING_TEXT]: Yup.string().when([FORM_IDS.OUTBOUND_TEXT_OR_RECORDED_MESSAGE], {
          is: 'text_message',
          then: Yup.string().required('Please enter a message'),
        }),
        [FORM_IDS.OUTBOUND_VOICE_FILE]: Yup.mixed().when([FORM_IDS.OUTBOUND_TEXT_OR_RECORDED_MESSAGE], {
          is: 'uploaded_text_message',
          then: allowSavingWithoutNewFile ? Yup.mixed() : Yup.mixed().required('Please select a file'),
        }),
      })}
      onSubmit={handleSubmit}
    >
      {({ isSubmitting, handleSubmit: formikHandleSubmit }) => {
        return (
          <CardDialog
            title="Configure outbound phone greeting"
            isDialog
            maxWidth="sm"
            fullWidth
            onClose={onCancel}
            preventClose={isSubmitting}
          >
            <VoiceMessageConfigurationDialog
              voiceMessageConfiguration={configuration}
              textAreaLabel="Outbound call greeting"
              radioButtonGroupId={FORM_IDS.OUTBOUND_TEXT_OR_RECORDED_MESSAGE}
              textAreaId={FORM_IDS.OUTBOUND_GREETING_TEXT}
              voiceFileInputId={FORM_IDS.OUTBOUND_VOICE_FILE}
              onReplaceExistingRecording={() => {
                setAllowSavingWithoutNewFile(false);
              }}
            />
            <div className={classes.buttonsContainer}>
              <CancelButton disabled={isSubmitting} onClick={onCancel} />
              <Button
                variant="contained"
                color="primary"
                disabled={isSubmitting}
                onClick={(e) => {
                  e.preventDefault();
                  formikHandleSubmit();
                }}
              >
                Save
              </Button>
            </div>
          </CardDialog>
        );
      }}
    </Formik>
  );
};

interface OutboundGreetingConfigViewProps {
  profile: PhoneProfileInfo;
}

const OutboundGreetingConfigView: React.FC<OutboundGreetingConfigViewProps> = ({ profile }) => {
  const configuration = profile.outbound_call_message_configuration;

  return (
    <>
      <Typography variant="subtitle1">Configure outbound phone greeting</Typography>
      <Typography variant="caption">
        Upload a voice message or add a text message for the outbound phone greeting that the customer hears when
        receiving a call.
      </Typography>
      {profile.outbound_call_message_enabled && !isEmpty(configuration) ? (
        <div className="mt-20">
          <VoiceMessageDetailsPreview
            configurationType={configuration?.configuration_type || ''}
            messageTextTitle="Voicemail by text message"
            messageText={configuration?.message_text}
            uploadedTextMessageTitle="Voicemail by recording"
            audioUrl={configuration?.permanent_audio_file_url}
          />
        </div>
      ) : null}
    </>
  );
};

export { EditOutboundGreetingConfigDialog, OutboundGreetingConfigView };
